// Proves that the browser asking for the tickets is the one that bought them. The secret stays in this tab's
// sessionStorage, which survives the round trip to the payment gateway; only its SHA-256 goes with the order.
// ms-ticket-issuance shows the tickets once, and only to whoever sends the secret.

const STORAGE_PREFIX = 'st_purchase_secret:';

export interface PurchaseSecret {
    secret: string;
    // SHA-256 of the secret, lowercase hex: what POST /order/init receives
    hash: string;
}

const toBase64Url = (bytes: Uint8Array): string =>
    btoa(Array.from(bytes, byte => String.fromCharCode(byte)).join(''))
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '');

export const toHex = (buffer: ArrayBuffer): string =>
    Array.from(new Uint8Array(buffer), byte => byte.toString(16).padStart(2, '0')).join('');

// Null when the browser cannot create one (crypto.subtle exists only on HTTPS and localhost): the order is
// created anyway and its tickets are only emailed
export const createPurchaseSecret = async (): Promise<PurchaseSecret | null> => {
    try {
        if (!window.crypto?.subtle) return null;
        const bytes = new Uint8Array(32);
        window.crypto.getRandomValues(bytes);
        const secret = toBase64Url(bytes);
        const digest = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(secret));
        return { secret, hash: toHex(digest) };
    } catch {
        return null;
    }
};

export const rememberPurchaseSecret = (reference: string, secret: string): void => {
    try {
        sessionStorage.setItem(STORAGE_PREFIX + reference, secret);
    } catch {
        // Storage blocked (private mode, disabled site data): the tickets are still emailed
    }
};

export const readPurchaseSecret = (reference: string): string | null => {
    try {
        return sessionStorage.getItem(STORAGE_PREFIX + reference);
    } catch {
        return null;
    }
};
